/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Card,
  Col,
  Container,
  ListGroup,
  ProgressBar,
  Row,
  Spinner,
  Toast,
  ToastContainer,
} from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { captureSupabaseError } from '../../lib/sentry';
import type {
  Organisation,
  SupplierCompanyProfile,
  UserOrganisation,
} from '../../types/types';
import SupplierProfileBuilderStepGeneral from './SupplierProfileBuilderStepGeneral';
import SupplierProfileBuilderStepOperations from './SupplierProfileBuilderStepOperations';
import SupplierProfileBuilderStepCertifications from './SupplierProfileBuilderStepCertifications';
import SupplierProfileBuilderStepPolicies from './SupplierProfileBuilderStepPolicies';
import SupplierProfileBuilderStepFinancialIdentity from './SupplierProfileBuilderStepFinancialIdentity';
import SupplierProfileBuilderStepSupplyChainTransparency from './SupplierProfileBuilderStepSupplyChainTransparency';
import { gToastPosition, gToastSentiment } from '../../util/constst';

export interface RestrictedFieldsSet {
  ubo_details: string;
  credit_score_value: string;
  credit_score_provider: string;
  is_financials_public: boolean;
  sanction_screening_status?: string;
  last_financial_audit_date?: string;
}

type StepKey =
  | 'general'
  | 'operations'
  | 'certifications'
  | 'policies'
  | 'supply_chain'
  | 'financial';

const steps: { key: StepKey; label: string; icon: string }[] = [
  { key: 'general', label: 'General', icon: 'bi-building' },
  { key: 'operations', label: 'Operational', icon: 'bi-gear' },
  { key: 'certifications', label: 'Certifications', icon: 'bi-patch-check' },
  { key: 'policies', label: 'Policies', icon: 'bi-journal-text' },
  {
    key: 'supply_chain',
    label: 'Supply Chain',
    icon: 'bi-diagram-3',
  },
  {
    key: 'financial',
    label: 'Financial & Identity',
    icon: 'bi-shield-lock',
  },
];

const SupplierProfileBuilder_NEW: React.FC = () => {
  const navigate = useNavigate();
  const selectedOrganisation = useSelector(
    (state: any) => state.organisation.selected,
  ) as UserOrganisation | undefined;

  const organisation = selectedOrganisation?.organisation as
    | Organisation
    | undefined;
  const supplierId = organisation?.id;

  const [activeStep, setActiveStep] = useState<StepKey>('general');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const [, setChangesSaved] = useState(true);
  const [restrictedFields, setRestrictedFields] = useState<RestrictedFieldsSet>(
    {
      ubo_details: '',
      credit_score_value: '',
      credit_score_provider: '',
      is_financials_public: false,
      last_financial_audit_date: undefined,
    },
  );

  const [profile, setProfile] = useState<SupplierCompanyProfile>({
    supplier_id: supplierId || '',
    company_name: organisation?.name || '',
    registration_number: organisation?.registration_number || '',
    display_assesment_results: false,
    tier_1_visibility_level: 'unknown',
    tier_2_visibility_level: 'none',
    raw_material_origins: [],
  });
  const [operations, setOperations] = useState<any>({
    supplier_id: supplierId || '',
  });
  const [opCountries, setOpCountries] = useState<number[]>([]);
  const [countries, setCountries] = useState<
    { id: number; name: string; code: string }[]
  >([]);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [tempCountry, setTempCountry] = useState<number | null>(null);
  const [tempOriginCountry, setTempOriginCountry] = useState<string>('');
  const [certificationCount, setCertificationCount] = useState(0);
  const [policyCount, setPolicyCount] = useState(0);

  const activeStepIndex = useMemo(
    () => steps.findIndex((step) => step.key === activeStep),
    [activeStep],
  );
  const progressPercent = Math.round(
    ((activeStepIndex + 1) / steps.length) * 100,
  );

  useEffect(() => {
    if (!supplierId) return;
    const fetchData = async () => {
      setIsLoading(true);

      const [
        profRes,
        opRes,
        countryRes,
        countriesRes,
        certificationsCountRes,
        policiesCountRes,
      ] = await Promise.all([
        supabase
          .from('supplier_company_profiles')
          .select('*')
          .eq('supplier_id', supplierId)
          .maybeSingle(),
        supabase
          .from('supplier_company_operations')
          .select('*')
          .eq('supplier_id', supplierId)
          .maybeSingle(),
        supabase
          .from('supplier_operation_countries')
          .select('country_id')
          .eq('supplier_id', supplierId),
        supabase
          .from('countries')
          .select('id, name, code')
          .order('name', { ascending: true }),
        supabase
          .from('supplier_certifications')
          .select('id', { count: 'exact', head: true })
          .eq('supplier_id', supplierId),
        supabase
          .from('supplier_policies')
          .select('id', { count: 'exact', head: true })
          .eq('supplier_id', supplierId),
      ]);

      if (profRes.data) setProfile(profRes.data);
      if (opRes.data) setOperations(opRes.data);
      if (countriesRes.data) setCountries(countriesRes.data);
      if (countryRes.data)
        setOpCountries(countryRes.data.map((c) => c.country_id));
      if (certificationsCountRes.count !== null) {
        setCertificationCount(certificationsCountRes.count ?? 0);
      }
      if (policiesCountRes.count !== null) {
        setPolicyCount(policiesCountRes.count ?? 0);
      }

      if (profRes.data?.id) {
        const { data: restrictedData } = await supabase
          .from('supplier_company_profile_restricted_fields')
          .select('*')
          .eq('profile_id', profRes.data.id)
          .maybeSingle();
        if (restrictedData) setRestrictedFields(restrictedData);
      } else {
        const { data: createdProfile, error: createError } = await supabase
          .from('supplier_company_profiles')
          .insert({
            supplier_id: supplierId,
            company_name: organisation?.name || profile.company_name || '',
            registration_number:
              organisation?.registration_number ||
              profile.registration_number ||
              '',
            display_assesment_results:
              profile.display_assesment_results ?? false,
          })
          .select('*')
          .single();

        if (createError) {
          captureSupabaseError(createError, {
            op: 'create supplier company profile',
          });
          setIsLoading(false);
          return;
        }

        if (createdProfile) {
          setProfile(createdProfile);
          const { data: restrictedData } = await supabase
            .from('supplier_company_profile_restricted_fields')
            .select('*')
            .eq('profile_id', createdProfile.id)
            .maybeSingle();
          if (restrictedData) setRestrictedFields(restrictedData);
        }
      }

      setIsLoading(false);
    };

    fetchData();
  }, [supplierId]);

  const handleSaveGeneral = async (advance = false) => {
    setErrors({});

    if (!profile.company_name?.trim()) {
      setErrors((prev) => ({
        ...prev,
        company_name: 'Company name is required',
      }));
      return false;
    }
    if (!profile.registration_number?.trim()) {
      setErrors((prev) => ({
        ...prev,
        registration_number: 'Registration Number is required',
      }));
      return false;
    }
    if (!profile.headquarters_location) {
      setErrors((prev) => ({
        ...prev,
        headquarters_location: 'Country is required',
      }));
      return false;
    }
    if (!profile.industry_id) {
      setErrors((prev) => ({ ...prev, industry_id: 'Industry is required' }));
      return false;
    }

    setIsSaving(true);

    const { error } = await supabase.from('supplier_company_profiles').upsert(
      {
        ...profile,
        supplier_id: supplierId,
        updated_at: new Date().toISOString(),
      },
      { onConflict: 'supplier_id' },
    );

    if (error) {
      alert(`Error: ${error.message}`);
      captureSupabaseError(error, { op: 'upsert supplier profile' });
      setIsSaving(false);
      return false;
    }

    setShowSuccessToast(true);
    setChangesSaved(true);
    if (advance) setActiveStep('operations');
    setIsSaving(false);
    return true;
  };

  const handleSaveOperations = async (advance = false) => {
    setErrors({});
    if (!operations.employee_count) {
      setErrors((prev) => ({
        ...prev,
        employee_count: 'Employee count is required',
      }));
      return false;
    }

    if (!operations.revenue_range) {
      setErrors((prev) => ({
        ...prev,
        revenue_range: 'Revenue range is required',
      }));
      return false;
    }

    if (opCountries.length === 0) {
      setErrors((prev) => ({
        ...prev,
        opCountries: 'At least one country of operation is required',
      }));
      return false;
    }

    setIsSaving(true);

    const { error: opError } = await supabase
      .from('supplier_company_operations')
      .upsert(
        {
          ...operations,
          supplier_id: supplierId,
          updated_at: new Date().toISOString(),
        },
        { onConflict: 'supplier_id' },
      );

    if (opError) {
      alert(`Error saving operations: ${opError.message}`);
      captureSupabaseError(opError, { op: 'upsert supplier operations' });
      setIsSaving(false);
      return false;
    }

    const { error: deleteError } = await supabase
      .from('supplier_operation_countries')
      .delete()
      .eq('supplier_id', supplierId)
      .not('country_id', 'in', `(${opCountries.join(',')})`);

    if (deleteError) {
      alert(`Error clearing old operation countries: ${deleteError.message}`);
      captureSupabaseError(deleteError, {
        op: 'delete old operation countries',
      });
      setIsSaving(false);
      return false;
    }

    const newRows = opCountries.map((id) => ({
      supplier_id: supplierId,
      country_id: id,
    }));

    const { error: countryError } = await supabase
      .from('supplier_operation_countries')
      .upsert(newRows, {
        onConflict: 'supplier_id,country_id',
      });

    let profileError: { message: string } | null = null;
    if (profile?.founded_year !== undefined) {
      const { error } = await supabase
        .from('supplier_company_profiles')
        .update({
          founded_year: profile.founded_year,
          updated_at: new Date().toISOString(),
        })
        .eq('supplier_id', supplierId);
      profileError = error;
    }

    if (countryError || profileError) {
      if (profileError) {
        alert(`Error saving founded year: ${profileError.message}`);
        captureSupabaseError(profileError, {
          op: 'update supplier profile founded_year',
        });
      }
      if (countryError) {
        alert(`Error saving operation countries: ${countryError.message}`);
        captureSupabaseError(countryError, {
          op: 'upsert operation countries',
        });
      }
      setIsSaving(false);
      return false;
    }

    setShowSuccessToast(true);
    setChangesSaved(true);
    if (advance) setActiveStep('certifications');
    setIsSaving(false);
    return true;
  };

  const handleSaveRestricted = async (advanceTo?: StepKey) => {
    // if (!profile?.id) {
    //   alert('Please save Company Info before saving restricted fields.');
    //   return false;
    // }
    setIsSaving(true);
    const { error } = await supabase
      .from('supplier_company_profile_restricted_fields')
      .upsert(
        {
          ...restrictedFields,
          profile_id: profile.id,
          updated_at: new Date().toISOString(),
        },
        { onConflict: 'profile_id' },
      );

    if (error) {
      alert(`Error saving restricted fields: ${error.message}`);
      captureSupabaseError(error, { op: 'upsert restricted fields' });
      setIsSaving(false);
      return false;
    }

    setShowSuccessToast(true);
    setChangesSaved(true);
    if (advanceTo) setActiveStep(advanceTo);
    setIsSaving(false);
    return true;
  };

  const handleSaveSupplyChain = async () => {
    setIsSaving(true);

    const { error } = await supabase
      .from('supplier_company_profiles')
      .update({
        tier_1_visibility_level: profile.tier_1_visibility_level || 'unknown',
        tier_2_visibility_level: profile.tier_2_visibility_level || 'none',
        raw_material_origins: profile.raw_material_origins || [],
        updated_at: new Date().toISOString(),
      })
      .eq('supplier_id', supplierId);

    if (error) {
      alert(`Error saving supply chain: ${error.message}`);
      captureSupabaseError(error, { op: 'update supply chain fields' });
      setIsSaving(false);
      return false;
    }

    setShowSuccessToast(true);
    setChangesSaved(true);
    setIsSaving(false);
    return true;
  };

  const addCountry = () => {
    if (tempCountry && !opCountries.includes(tempCountry)) {
      setOpCountries([...opCountries, tempCountry]);
      setTempCountry(null);
      setChangesSaved(false);
    }
  };

  const removeCountry = (id: number) => {
    setOpCountries(opCountries.filter((c) => c !== id));
    setChangesSaved(false);
  };

  const addOriginCountry = () => {
    if (
      tempOriginCountry &&
      !profile.raw_material_origins?.includes(tempOriginCountry)
    ) {
      setProfile({
        ...profile,
        raw_material_origins: [
          ...(profile?.raw_material_origins || []),
          tempOriginCountry,
        ],
      });
      setTempOriginCountry('');
      setChangesSaved(false);
    }
  };

  const getStepStatus = (stepKey: StepKey) => {
    if (stepKey === activeStep) return 'current';

    switch (stepKey) {
      case 'general':
        return profile.company_name &&
          profile.registration_number &&
          profile.headquarters_location &&
          profile.industry_id
          ? 'complete'
          : 'pending';
      case 'operations':
        return operations.employee_count &&
          operations.revenue_range &&
          opCountries.length > 0
          ? 'complete'
          : 'pending';
      case 'certifications':
        return certificationCount > 0 ? 'complete' : 'pending';
      case 'policies':
        return policyCount > 0 ? 'complete' : 'pending';
      case 'financial':
        return restrictedFields.ubo_details ||
          restrictedFields.credit_score_value ||
          restrictedFields.credit_score_provider ||
          restrictedFields.last_financial_audit_date ||
          restrictedFields.sanction_screening_status !== 'not_screened' ||
          restrictedFields.is_financials_public
          ? 'complete'
          : 'pending';
      case 'supply_chain':
        return profile.tier_1_visibility_level !== 'unknown' ||
          profile.tier_2_visibility_level !== 'none' ||
          (profile.raw_material_origins?.length ?? 0) > 0
          ? 'complete'
          : 'pending';
      default:
        return 'pending';
    }
  };

  const handleContinue = async () => {
    switch (activeStep) {
      case 'general':
        await handleSaveGeneral(true);
        break;
      case 'operations':
        await handleSaveOperations(true);
        break;
      case 'certifications':
        setActiveStep('policies');
        break;
      case 'policies':
        setActiveStep('supply_chain');
        break;
      case 'supply_chain':
        await handleSaveSupplyChain();
        setActiveStep('financial');
        break;
      case 'financial':
        if (await handleSaveRestricted()) {
          navigate('/my-profile');
        }
        break;
      default:
        break;
    }
  };

  const handleSaveDraft = async () => {
    switch (activeStep) {
      case 'general':
        await handleSaveGeneral(false);
        break;
      case 'operations':
        await handleSaveOperations(false);
        break;
      case 'financial':
        await handleSaveRestricted();
        break;
      case 'supply_chain':
        await handleSaveSupplyChain();
        break;
      default:
        break;
    }
  };

  if (!supplierId) {
    return (
      <Container className="py-4">
        <Card>
          <Card.Body>
            <h4 className="mb-2">Supplier Profile Builder</h4>
            <p className="mb-3 text-muted">
              Select an organisation to start building your public profile.
            </p>
            <Button variant="outline-primary" onClick={() => navigate(-1)}>
              Go Back
            </Button>
          </Card.Body>
        </Card>
      </Container>
    );
  }

  return (
    <Container fluid className="pb-4">
      <Row className="mb-4">
        <Col>
          <div className="d-flex align-items-center justify-content-between">
            <div>
              <h2 className="mb-1">Supplier Profile Builder</h2>
              <p className="text-muted mb-0">
                Build your public compliance profile step by step.
              </p>
            </div>
            <Button variant="outline-secondary" onClick={() => navigate(-1)}>
              <i className="bi bi-arrow-left me-2"></i>
              Back
            </Button>
          </div>
        </Col>
      </Row>

      <Row>
        <Col lg={3} className="mb-4">
          <Card className="shadow-sm">
            <Card.Body>
              <div className="d-flex align-items-center justify-content-between mb-2">
                <span className="text-muted">Progress</span>
                <span className="fw-semibold">{progressPercent}%</span>
              </div>
              <ProgressBar now={progressPercent} className="mb-3" />
              <ListGroup variant="flush">
                {steps.map((step) => {
                  const status = getStepStatus(step.key);
                  const statusIcon =
                    status === 'complete'
                      ? `bi-check-circle-fill ${
                          step.key === activeStep
                            ? 'text-white'
                            : 'text-success'
                        }`
                      : 'bi-circle text-muted';

                  return (
                    <ListGroup.Item
                      key={step.key}
                      action
                      active={activeStep === step.key}
                      onClick={() => setActiveStep(step.key)}
                      className="d-flex align-items-center justify-content-between"
                    >
                      <div className="d-flex align-items-center gap-2">
                        <i className={`bi ${step.icon}`}></i>
                        <span>{step.label}</span>
                      </div>
                      <i className={`bi ${statusIcon}`}></i>
                    </ListGroup.Item>
                  );
                })}
              </ListGroup>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={9}>
          <Card className="shadow-sm">
            <Card.Header className="bg-white">
              <div className="d-flex align-items-center gap-2">
                <i className={`bi ${steps[activeStepIndex]?.icon}`}></i>
                <h5 className="mb-0">{steps[activeStepIndex]?.label}</h5>
              </div>
            </Card.Header>
            <Card.Body>
              {isLoading ? (
                <div className="text-center py-5">
                  <Spinner animation="border" />
                </div>
              ) : (
                <div>
                  {activeStep === 'general' && (
                    <SupplierProfileBuilderStepGeneral
                      organisation={organisation}
                      profile={profile}
                      setProfile={setProfile}
                      errors={errors}
                      setChangesSaved={setChangesSaved}
                    />
                  )}
                  {activeStep === 'operations' && (
                    <SupplierProfileBuilderStepOperations
                      operations={operations}
                      setOperations={setOperations}
                      profile={profile}
                      setProfile={setProfile}
                      opCountries={opCountries}
                      countries={countries}
                      tempCountry={tempCountry}
                      setTempCountry={setTempCountry}
                      errors={errors}
                      setChangesSaved={setChangesSaved}
                      addCountry={addCountry}
                      removeCountry={removeCountry}
                    />
                  )}
                  {activeStep === 'certifications' && (
                    <SupplierProfileBuilderStepCertifications
                      supplierId={supplierId}
                      onCountChange={setCertificationCount}
                    />
                  )}
                  {activeStep === 'policies' && (
                    <SupplierProfileBuilderStepPolicies
                      supplierId={supplierId}
                      onCountChange={setPolicyCount}
                    />
                  )}
                  {activeStep === 'supply_chain' && (
                    <SupplierProfileBuilderStepSupplyChainTransparency
                      profile={profile}
                      setProfile={setProfile}
                      tempOriginCountry={tempOriginCountry}
                      setTempOriginCountry={setTempOriginCountry}
                      setChangesSaved={setChangesSaved}
                      addOriginCountry={addOriginCountry}
                      countries={countries}
                    />
                  )}
                  {activeStep === 'financial' && (
                    <SupplierProfileBuilderStepFinancialIdentity
                      restrictedFields={restrictedFields}
                      setRestrictedFields={setRestrictedFields}
                      setChangesSaved={setChangesSaved}
                    />
                  )}
                </div>
              )}
            </Card.Body>
            <Card.Footer className="bg-white d-flex justify-content-between">
              <Button
                variant="outline-secondary"
                onClick={handleSaveDraft}
                disabled={
                  isSaving ||
                  activeStep === 'certifications' ||
                  activeStep === 'policies'
                }
              >
                <i className="bi bi-save me-2"></i>
                Save draft
              </Button>
              <Button
                variant="primary"
                onClick={handleContinue}
                disabled={isSaving}
              >
                {isSaving ? (
                  'Saving...'
                ) : activeStep === 'financial' ? (
                  'Finish'
                ) : (
                  <>
                    Continue
                    <i className="bi bi-arrow-right ms-2"></i>
                  </>
                )}
              </Button>
            </Card.Footer>
          </Card>
        </Col>
      </Row>

      {showSuccessToast && (
        <ToastContainer position={gToastPosition} className="p-3">
          <Toast
            onClose={() => setShowSuccessToast(false)}
            show={showSuccessToast}
            delay={3000}
            autohide
            bg={gToastSentiment}
          >
            <Toast.Body>Changes saved successfully.</Toast.Body>
          </Toast>
        </ToastContainer>
      )}
    </Container>
  );
};

export default SupplierProfileBuilder_NEW;
